
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import SettingsTitleBar from '@components/settings/SettingsTitleBar.vue';
import SettingsPanel from '@components/settings/SettingsPanel.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppEditableHeader from '@components/new-styleguide/AppEditableHeader.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import RuleTabs from '@components/settings/rule/RuleTabs.vue';
import { RuleTriggerEnum, RuleV2Mutation, TripLog } from '@/generated-clients/myTrips';
import { LoadState } from '@/utils/types';
import goBackWithFallback from '@util/goBackWithFallback';
import { RouterFrom } from '@/router/Routes';
import { Location } from 'vue-router';

const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: 'RuleAdd',
  components: {
    AppPage,
    AppContent,
    AppEditableHeader,
    InfoBoxWarning,
    SettingsPanel,
    SettingsFooter,
    SettingsTitleBar,
    RuleTabs
  }
})
export default class RuleAdd extends Vue {
  @SettingsModule.Action('createRuleV2')
  private createRule;

  @TriplogModule.Getter('getVehiclesStatus')
  private vehiclesStatus: LoadState;

  @TriplogModule.Action('fetchVehicles')
  private fetchVehicles;

  @TriplogModule.Getter('getVehicles')
  private tripLogs: TripLog[];

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Getter('getPilots')
  private pilots;

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Getter('getTags')
  private tags;

  @SettingsModule.Action('fetchZones')
  private fetchZones;

  @SettingsModule.Getter('getZones')
  private zones;

  @SettingsModule.Getter('getTagsStatus')
  private tagsStatus: LoadState;

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @SettingsModule.Getter('getZonesStatus')
  private zonesStatus: LoadState;

  @RouterModule.State('from')
  private from: RouterFrom;

  private rule: RuleV2Mutation = {
    name: '',
    active: true,
    channels: [],
    vehicleIds: [],
    triggers: [RuleTriggerEnum.TripStart],
    zoneIds: [],
    pilotId: null,
    tagId: null,
    work: false,
    note: '',
    triggerTimeframe: null
  }

  private disabled = false;
  private requestErrorMessage = '';

  created() {
    this.fetchVehicles();
    this.fetchPilots();
    this.fetchTags();
    this.fetchZones();
  }

  get backButton() {
    return { text: 'Zurück zu Regeln', location: this.backLocation };
  }

  get backLocation(): Location {
    return { name: 'rulesOverview' };
  }

  get nameError() {
    return this.rule.name.length === 0;
  }

  get noActionSelectedError() {
    return !this.rule.pilotId && !this.rule.tagId && !this.rule.note && (this.rule.channels.length === 0 || !this.notificationsAvailable);
  }

  get errorsExist() {
    return this.nameError || !!this.customErrorMessage;
  }

  get customErrorAndNoTitleErrorExists() {
    return !this.nameError && !!this.customErrorMessage;
  }

  get customErrorMessage() {
    if (this.rule.triggers.length === 0) {
      return 'Es muss mindestens ein Auslöser ausgewählt sein (Abfahrt oder Ankunft).';
    } else if (this.noActionSelectedError) {
      return 'Es muss mindestens eine Aktion ausgewählt sein.';
    } else if (this.rule.work && !this.rule.note) {
      return 'Bei einer beruflichen Fahrt ist die Beschreibung erforderlich.';
    } else if (this.timeframeError) {
      return 'Die ausgewählte Zeitspanne ist nicht gültig.';
    }

    return undefined;
  }

  get timeframeError() {
    return this.rule.triggerTimeframe && (Boolean(this.rule.triggerTimeframe.from) !== Boolean(this.rule.triggerTimeframe.until) ||
      this.rule.triggerTimeframe.from === this.rule.triggerTimeframe.until);
  }

  private async submit() {
    if (!this.errorsExist) {
      this.disabled = true;
      this.requestErrorMessage = '';

      const zones = this.rule.zoneIds.length > 0 ? this.rule.zoneIds : this.zones.map(zone => zone.id)

      if (!this.notificationsAvailable) {
        this.rule.channels = [];
      }

      try {
        await this.createRule({ rule: { ...this.rule, vehicleIds: this.selectedVehicles, zoneIds: zones } });
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } catch (error) {
        if (error.response.status === 409) {
          this.requestErrorMessage = 'Diese Regel kann nicht gespeichert werden, da sie Im Konflikt mit einer anderen Regel steht.';
        } else {
          this.requestErrorMessage = 'Fehler beim Speichern der Regel. Bitte versuchen sie es später erneut.';
        }
      } finally {
        this.disabled = false;
      }
    }
  }

  get notificationsAvailable() {
    return this.tripLogs
        .filter(tripLog => tripLog.smartConnected)
        .some(smartConnectedTripLog => this.selectedVehicles.includes(smartConnectedTripLog.vehicle.id));
  }

  get selectedVehicles() {
    return this.rule.vehicleIds.length > 0 ? this.rule.vehicleIds : this.tripLogs.map(tl => tl.vehicle.id);
  }

  get loadingStatuses() {
    return [this.vehiclesStatus, this.tagsStatus, this.pilotsStatus, this.zonesStatus];
  }

  get isLoading() {
    return this.loadingStatuses.some(status => status === LoadState.Loading || status === LoadState.None);
  }

  get errorLoading() {
    return this.loadingStatuses.some(status => status === LoadState.Error);
  }
}
